/**
 * useMfaEnroll — admin + tenant variants for ``POST .../mfa/enroll``.
 *
 * Returns React Query mutations that POST with the mfa_pending_token
 * from ``useAuthStore`` as the explicit Bearer header (NOT via the
 * apiClient interceptor — the interceptor reads ``accessToken`` which
 * is null while we're in the mfa_pending state).
 *
 * Response: ``{credential_id, totp_secret, provisioning_uri, recovery_codes[]}``.
 */
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";

export interface MfaEnrollResponse {
  credential_id: string;
  totp_secret: string;
  provisioning_uri: string;
  recovery_codes: string[];
}

function authHeader(): { Authorization: string } {
  const token = useAuthStore.getState().mfaPendingToken;
  if (!token) {
    // Caller should never reach the enroll page without a pending token.
    // Throwing keeps the failure mode loud and prevents an
    // unauthenticated POST.
    throw new Error("mfaPendingToken missing from store");
  }
  return { Authorization: `Bearer ${token}` };
}

export function useAdminMfaEnroll() {
  return useMutation<MfaEnrollResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<MfaEnrollResponse>(
        "/admin/mfa/enroll",
        undefined,
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}

export function useTenantMfaEnroll(tenantSlug: string) {
  return useMutation<MfaEnrollResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<MfaEnrollResponse>(
        `/api/t/${encodeURIComponent(tenantSlug)}/auth/mfa/enroll`,
        undefined,
        { headers: authHeader() },
      );
      return resp.data;
    },
  });
}
